import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";

interface UseColumnOrderSaveOptions {
  columnKeys: string[];
  localStorageKey: string;
  defaultOrder: string[];
}

// Global state to track unsaved changes across all instances
const globalUnsavedChanges = new Map<string, boolean>();
const globalNavigationBlockers = new Map<string, {
  showModal: () => void;
  save: () => void;
  discard: () => void;
}>();

export const useColumnOrderSave = ({
  columnKeys,
  localStorageKey,
  defaultOrder,
}: UseColumnOrderSaveOptions) => {
  const [showSaveModal, setShowSaveModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const initialOrderRef = useRef<string[]>([]);
  const hasChangesRef = useRef(false);
  const isInitializedRef = useRef(false);
  const previousLocationRef = useRef(location.pathname);
  const pendingNavigationRef = useRef<string | null>(null);
  const isNavigatingBackRef = useRef(false);
  const columnKeysRef = useRef<string[]>(columnKeys);

  // Update ref when columnKeys changes
  useEffect(() => {
    columnKeysRef.current = columnKeys;
  }, [columnKeys]);

  // Initialize with the current columnKeys prop value once
  useEffect(() => {
    if (!isInitializedRef.current) {
      if (columnKeys.length > 0) {
        initialOrderRef.current = [...columnKeys];
        isInitializedRef.current = true;
        previousLocationRef.current = location.pathname;
        console.log("✅ Hook initialized with columnKeys:", columnKeys.length, "items");
      }
    }
  }, [columnKeys, location.pathname]);

  // Track if column order has changed from initial
  useEffect(() => {
    if (!isInitializedRef.current || columnKeys.length === 0) {
      return;
    }

    const currentOrder = JSON.stringify(columnKeys);
    const initialOrder = JSON.stringify(initialOrderRef.current);
    const hasChanges = currentOrder !== initialOrder;
    
    // Update global state
    globalUnsavedChanges.set(localStorageKey, hasChanges);
    
    if (hasChangesRef.current !== hasChanges) {
      hasChangesRef.current = hasChanges;
      if (hasChanges) {
        console.log("⚠️ UNSAVED CHANGES DETECTED!");
      }
    }
  }, [columnKeys, localStorageKey]);

  const handleSave = () => {
    const keysToSave = columnKeys.length > 0 ? [...columnKeys] : [...columnKeysRef.current];
    
    console.log("=== handleSave called ===");
    console.log("Saving", keysToSave.length, "columns");
    
    const savedValue = JSON.stringify(keysToSave);
    try {
      localStorage.setItem(localStorageKey, savedValue);
      console.log("✅ Saved to localStorage:", localStorageKey);
      toast.success("Column order saved successfully!");
      const verification = localStorage.getItem(localStorageKey);
      if (verification === savedValue) {
        console.log("✅ Verification passed");
      } else {
        console.error("❌ Verification failed!");
      }
    } catch (error) {
      console.error("❌ ERROR saving to localStorage:", error);
    }
    
    initialOrderRef.current = keysToSave;
    hasChangesRef.current = false;
    globalUnsavedChanges.set(localStorageKey, false);
    setShowSaveModal(false);
    
    const pendingPath = pendingNavigationRef.current;
    pendingNavigationRef.current = null;
    if (pendingPath) {
      previousLocationRef.current = location.pathname;
      navigate(pendingPath);
    }
  };

  const handleDiscard = () => {
    hasChangesRef.current = false;
    globalUnsavedChanges.set(localStorageKey, false);
    setShowSaveModal(false);
    
    const pendingPath = pendingNavigationRef.current;
    pendingNavigationRef.current = null;
    if (pendingPath) {
      previousLocationRef.current = location.pathname;
      navigate(pendingPath);
    }
  };

  // Register global navigation blocker
  useEffect(() => {
    globalNavigationBlockers.set(localStorageKey, {
      showModal: () => setShowSaveModal(true),
      save: handleSave,
      discard: handleDiscard,
    });

    return () => {
      globalNavigationBlockers.delete(localStorageKey);
      globalUnsavedChanges.delete(localStorageKey);
    };
  }, [localStorageKey, navigate]);

  // Intercept Link clicks globally
  useEffect(() => {
    // 🎨 Inject Custom Styles for Blue Pulse Animation
    const styleId = "save-modal-styles";
    if (!document.getElementById(styleId)) {
      const styleSheet = document.createElement("style");
      styleSheet.id = styleId;
      styleSheet.innerText = `
        @keyframes pulseGlow {
          0%   { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.55); }
          70%  { box-shadow: 0 0 0 14px rgba(220, 53, 69, 0); }
          100% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0); }
        }
        .effect-avatar-pulse {
          animation: pulseGlow 2.1s infinite ease-out;
          border-radius: 50%;
        }
        /* Custom Close Button Style */
        .custom-btn-close {
           width: 24px;
           height: 24px;
           font-size: 12px;
           background-size: 10px;
        }
      `;
      document.head.appendChild(styleSheet);
    }

    const handleLinkClick = (e: MouseEvent) => {
      // Check if there are any unsaved changes globally
      const hasAnyUnsavedChanges = Array.from(globalUnsavedChanges.values()).some(v => v === true);
      
      if (!hasAnyUnsavedChanges) return;
      
      const target = e.target as HTMLElement;
      const link = target.closest('a[href]') as HTMLAnchorElement;
      
      if (link && link.href) {
        // Check if it's an internal navigation (not external link)
        const href = link.getAttribute('href');
        const isInternalLink = href && (
          href.startsWith('/') || 
          href.startsWith('#/') ||
          (href.startsWith('#') && href.length > 1 && href[1] !== '#')
        );
        
        if (isInternalLink && hasChangesRef.current) {
          e.preventDefault();
          e.stopPropagation();
          
          let targetPath = href;
          if (href.startsWith('#/')) {
            targetPath = href.substring(1);
          } else if (href.startsWith('#')) {
            targetPath = href.substring(1);
          }
          
          console.log("🚨 Link click intercepted with unsaved changes!");
          console.log("Target path:", targetPath);
          console.log("Original href:", href);
          
          pendingNavigationRef.current = targetPath;
          setShowSaveModal(true);
        }
      }
    };

    document.addEventListener('click', handleLinkClick, true);
    return () => document.removeEventListener('click', handleLinkClick, true);
  }, []);

  // Intercept navigate calls
  useEffect(() => {
    const originalNavigate = navigate;
    const navigateWithCheck = ((to: any, options?: any) => {
      if (hasChangesRef.current && typeof to === 'string') {
        console.log("🚨 Navigate call intercepted with unsaved changes!");
        console.log("Target:", to);
        
        pendingNavigationRef.current = to;
        setShowSaveModal(true);
        return; // Don't navigate yet
      }
      return originalNavigate(to, options);
    }) as typeof navigate;

    // Note: We can't override navigate, so we'll rely on location change detection
  }, [navigate]);

  // Detect navigation attempts when there are unsaved changes
  useEffect(() => {
    if (!isInitializedRef.current) {
      previousLocationRef.current = location.pathname;
      return;
    }

    const currentPath = location.pathname;
    const previousPath = previousLocationRef.current;

    if (isNavigatingBackRef.current) {
      isNavigatingBackRef.current = false;
      previousLocationRef.current = currentPath;
      return;
    }

    if (
      currentPath !== previousPath &&
      hasChangesRef.current &&
      !showSaveModal &&
      !pendingNavigationRef.current
    ) {
      console.log("🚨 Navigation detected with unsaved changes!");
      console.log("From:", previousPath, "To:", currentPath);
      
      pendingNavigationRef.current = currentPath;
      setShowSaveModal(true);
      
      isNavigatingBackRef.current = true;
      navigate(previousPath, { replace: true });
    } else if (currentPath !== previousPath && !hasChangesRef.current) {
      previousLocationRef.current = currentPath;
      pendingNavigationRef.current = null;
    } else if (currentPath === previousPath && pendingNavigationRef.current && !showSaveModal) {
      pendingNavigationRef.current = null;
    }
  }, [location.pathname, navigate, showSaveModal]);

  // Note: Refresh functionality removed - refresh will not prompt and changes will be lost

  const handleCancel = () => {
    setShowSaveModal(false);
    pendingNavigationRef.current = null;
  };

  const SaveModal = () => {
    // 🖱️ Draggable Logic
    useEffect(() => {
      if (showSaveModal) {
        const setupDraggable = (modalId: string) => {
          const modal = document.getElementById(modalId);
          const header = modal?.querySelector(".modal-header") as HTMLElement;
          if (modal && header) {
            header.style.cursor = "move";
            let isDragging = false, startX = 0, startY = 0, initialLeft = 0, initialTop = 0;

            const onMouseDown = (e: MouseEvent) => {
              isDragging = true;
              startX = e.clientX;
              startY = e.clientY;
              const rect = modal.getBoundingClientRect();
              initialLeft = rect.left;
              initialTop = rect.top;
              modal.style.position = "absolute";
              modal.style.margin = "0";
              modal.style.left = `${initialLeft}px`;
              modal.style.top = `${initialTop}px`;
              document.addEventListener("mousemove", onMouseMove);
              document.addEventListener("mouseup", onMouseUp);
            };

            const onMouseMove = (e: MouseEvent) => {
              if (!isDragging) return;
              const dx = e.clientX - startX;
              const dy = e.clientY - startY;
              modal.style.left = `${initialLeft + dx}px`;
              modal.style.top = `${initialTop + dy}px`;
            };

            const onMouseUp = () => {
              isDragging = false;
              document.removeEventListener("mousemove", onMouseMove);
              document.removeEventListener("mouseup", onMouseUp);
            };

            header.addEventListener("mousedown", onMouseDown);
            return () => header.removeEventListener("mousedown", onMouseDown);
          }
        };
        // Small timeout to ensure DOM is ready
        setTimeout(() => setupDraggable("save_column_order_modal"), 100);
      }
    }, [showSaveModal]);

    return (
      <div
        className={`modal fade ${showSaveModal ? "show d-block" : ""}`}
        id="save_column_order_modal"
        tabIndex={-1}
        role="dialog"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 shadow-lg">

            {/* 🔴 Header with Border */}
            <div className="modal-header bg-white">
              <h5 className="modal-title text-danger fw-bold">
                <i className="ti ti-alert-triangle me-2" />
                Unsaved Changes
              </h5>
              <button
                type="button"
                className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
                onClick={handleCancel}
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>

            {/* 🟡 Body */}
            <div className="modal-body text-center pt-4 pb-4">
              {/* Red Pulse Avatar */}
              <div className="avatar avatar-xl bg-danger-light rounded-circle mb-4 mx-auto effect-avatar-pulse">
                <i className="ti ti-device-floppy fs-36 text-danger" />
              </div>

              <h4 className="fw-bold text-dark mb-3">Save Column Order?</h4>

              <p className="text-muted fs-15 mb-4 px-4">
                You have changed the column layout. Do you want to save these changes for future visits?
              </p>

              <div className="alert alert-warning py-2 mx-4 mb-4 rounded-3 border-warning d-flex align-items-center justify-content-center">
                <i className="ti ti-alert-circle me-2 fs-5" />
                <span className="fs-14">Leaving without saving will lose changes.</span>
              </div>

              {/* 🔴 Footer Actions */}
              <div className="d-flex gap-3 justify-content-center mt-2">
                {/* <button
                  className="btn btn-light px-4 py-2 fw-medium"
                  onClick={handleCancel}
                >
                  Cancel
                </button> */}
                <button
                  className="btn btn-danger px-4 py-2 fw-medium"
                  onClick={handleDiscard}
                  style={{ minWidth: "120px" }}
                >
                  Don't Save
                </button>
                <button
                  className="btn btn-success px-4 py-2 fw-medium"
                  onClick={handleSave}
                  style={{ minWidth: "120px" }}
                >
                  Save Changes
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>
    );
  };

  return { SaveModal };
};
